import { useMemo } from "react";
import {
  Activity,
  LineChart as LineChartIcon,
  TrendingUp,
  UserX,
} from "lucide-react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
} from "recharts";
import { SafeChart } from "@/components/SafeChart";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  buildChurnChartRows,
  buildCumulativePeriodChartRows,
  buildPeriodChartRows,
  buildWeeklyChartRows,
  computeDailyAverageCombined,
  formatChartDateLong,
  formatChartDateShort,
  formatInvestorMoney,
  formatPeriodRange,
  INVESTOR_TRACKING_LABEL,
  INVESTOR_TRACKING_START,
  getCurrentMeasurementPeriod,
  getPeriodStatusFromRange,
  type DailyVolumeRow,
  type ReportingPeriodRevenueRow,
  type SubscriptionChurnPeriodRow,
  type WeeklyRevenueRow,
} from "@/lib/investorKpiDisplay";
import { cn } from "@/lib/utils";

function formatUsdAxis(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return `$${Math.round(value)}`;
}

function formatPctAxis(value: number): string {
  return `${value}%`;
}

type Props = {
  dailyVolume: DailyVolumeRow[];
  reportingPeriodRevenue: ReportingPeriodRevenueRow[];
  weeklyRevenue: WeeklyRevenueRow[];
  subscriptionChurnByPeriod: SubscriptionChurnPeriodRow[];
  exchangeRate: number;
  trackingCombinedUsd: number;
};

export function InvestorProgressCharts({
  dailyVolume,
  reportingPeriodRevenue,
  weeklyRevenue,
  subscriptionChurnByPeriod,
  exchangeRate,
  trackingCombinedUsd,
}: Props) {
  const currentPeriodId = getCurrentMeasurementPeriod().id;

  const dailyChart = useMemo(
    () =>
      dailyVolume.map((d) => ({
        date: d.date,
        usdNative: d.revenue_usd,
        arsAsUsd: Number((d.revenue_ars / exchangeRate).toFixed(2)),
        combined: d.revenue_combined_usd,
        cumulative: d.cumulative_combined_usd,
        transactions: d.transactions,
      })),
    [dailyVolume, exchangeRate],
  );

  const dailyAvg = useMemo(() => computeDailyAverageCombined(dailyVolume), [dailyVolume]);
  const dailyTickInterval = dailyChart.length > 45 ? Math.floor(dailyChart.length / 12) : dailyChart.length > 20 ? 2 : 0;

  const periodChart = useMemo(
    () => buildCumulativePeriodChartRows(reportingPeriodRevenue, currentPeriodId),
    [reportingPeriodRevenue, currentPeriodId],
  );

  const periodBars = useMemo(
    () => buildPeriodChartRows(reportingPeriodRevenue, currentPeriodId),
    [reportingPeriodRevenue, currentPeriodId],
  );

  const weeklyChart = useMemo(() => buildWeeklyChartRows(weeklyRevenue.slice(-12)), [weeklyRevenue]);

  const churnChart = useMemo(
    () => buildChurnChartRows(subscriptionChurnByPeriod, currentPeriodId),
    [subscriptionChurnByPeriod, currentPeriodId],
  );

  const hasDaily = dailyChart.length > 0;
  const hasPeriod = periodChart.some((p) => p.combinedUsd > 0);
  const hasWeekly = weeklyChart.some((w) => w.combinedUsd > 0);
  const hasChurn = churnChart.some((c) => c.activeAtEnd > 0 || c.newSubs > 0 || c.cancelled > 0);

  if (!hasDaily && !hasPeriod && !hasWeekly && !hasChurn) {
    return null;
  }

  const lastDaily = dailyChart[dailyChart.length - 1];
  const firstDaily = dailyChart[0];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2 text-foreground">
            <TrendingUp className="h-5 w-5 text-primary" />
            Progreso y tendencias
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
            Revenue real día a día, acumulado de tracking, cortes comerciales y salud de suscripciones.
            Total USD = nativo + ARS÷{exchangeRate.toLocaleString("es-AR")}.
          </p>
        </div>
        {hasDaily && lastDaily && (
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="tabular-nums text-[11px]">
              Acumulado: {formatInvestorMoney(lastDaily.cumulative, "USD")}
            </Badge>
            <Badge variant="outline" className="tabular-nums text-[11px]">
              Prom. diario: {formatInvestorMoney(dailyAvg, "USD")}
            </Badge>
          </div>
        )}
      </div>

      {hasDaily && (
        <Card className="glass-card border-primary/20 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              Revenue diario + acumulado
            </CardTitle>
            <CardDescription className="text-[11px]">
              {firstDaily && lastDaily
                ? `${formatChartDateLong(firstDaily.date)} → ${formatChartDateLong(lastDaily.date)} · ${dailyChart.length} días con actividad`
                : `Desde ${INVESTOR_TRACKING_START}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SafeChart>
              <ResponsiveContainer width="100%" height={380}>
                <ComposedChart data={dailyChart} margin={{ top: 12, right: 20, left: 4, bottom: 8 }}>
                  <defs>
                    <linearGradient id="invGradUsd" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.45} />
                    </linearGradient>
                    <linearGradient id="invGradArs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0.45} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v) => (typeof v === "string" ? formatChartDateShort(v) : String(v))}
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    interval={dailyTickInterval}
                    angle={dailyChart.length > 25 ? -35 : 0}
                    textAnchor={dailyChart.length > 25 ? "end" : "middle"}
                    height={dailyChart.length > 25 ? 52 : 28}
                    tickLine={false}
                    axisLine={{ stroke: "hsl(var(--border) / 0.5)" }}
                  />
                  <YAxis
                    yAxisId="daily"
                    tickFormatter={formatUsdAxis}
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    width={52}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, "auto"]}
                  />
                  <YAxis
                    yAxisId="cumulative"
                    orientation="right"
                    tickFormatter={formatUsdAxis}
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    width={56}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, "auto"]}
                  />
                  <Tooltip
                    cursor={{ fill: "hsl(var(--muted) / 0.25)" }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.[0]) return null;
                      const row = payload[0].payload as (typeof dailyChart)[number];
                      return (
                        <div className="min-w-[240px] rounded-lg border border-white/10 bg-black/90 p-3 text-sm shadow-xl backdrop-blur-sm">
                          <p className="font-semibold mb-2">{formatChartDateLong(row.date)}</p>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between gap-4">
                              <span className="text-muted-foreground">USD nativo</span>
                              <span className="font-medium tabular-nums">{formatInvestorMoney(row.usdNative, "USD")}</span>
                            </div>
                            <div className="flex justify-between gap-4">
                              <span className="text-muted-foreground">ARS→USD</span>
                              <span className="font-medium tabular-nums">{formatInvestorMoney(row.arsAsUsd, "USD")}</span>
                            </div>
                            <div className="flex justify-between gap-4 border-t pt-1 mt-1">
                              <span className="font-medium">Total día</span>
                              <span className="font-bold tabular-nums text-violet-600">{formatInvestorMoney(row.combined, "USD")}</span>
                            </div>
                            <div className="flex justify-between gap-4">
                              <span className="text-muted-foreground">Acumulado</span>
                              <span className="font-semibold tabular-nums text-amber-600">{formatInvestorMoney(row.cumulative, "USD")}</span>
                            </div>
                            <div className="flex justify-between gap-4 text-muted-foreground">
                              <span>Transacciones</span>
                              <span className="tabular-nums">{row.transactions}</span>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Legend
                    verticalAlign="top"
                    height={32}
                    iconType="circle"
                    formatter={(value) => {
                      if (value === "usdNative") return "USD Stripe";
                      if (value === "arsAsUsd") return `ARS÷${exchangeRate}`;
                      if (value === "cumulative") return "Acumulado tracking";
                      return value;
                    }}
                    wrapperStyle={{ fontSize: 11 }}
                  />
                  {dailyAvg > 0 && (
                    <ReferenceLine
                      yAxisId="daily"
                      y={dailyAvg}
                      stroke="#8b5cf6"
                      strokeDasharray="5 4"
                      strokeWidth={2}
                      label={{
                        value: `Prom. ${formatInvestorMoney(dailyAvg, "USD")}`,
                        position: "insideTopRight",
                        fill: "#8b5cf6",
                        fontSize: 10,
                        fontWeight: 600,
                      }}
                    />
                  )}
                  <Bar yAxisId="daily" dataKey="usdNative" stackId="day" fill="url(#invGradUsd)" name="usdNative" maxBarSize={28} />
                  <Bar yAxisId="daily" dataKey="arsAsUsd" stackId="day" fill="url(#invGradArs)" name="arsAsUsd" maxBarSize={28} radius={[4, 4, 0, 0]} />
                  <Line
                    yAxisId="cumulative"
                    type="monotone"
                    dataKey="cumulative"
                    stroke="#f59e0b"
                    strokeWidth={2.5}
                    dot={dailyChart.length <= 40 ? { r: 2, fill: "#f59e0b" } : false}
                    activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2 }}
                    name="cumulative"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </SafeChart>
            <p className="mt-2 text-[10px] text-muted-foreground">
              Meta tracking actual:{" "}
              <span className="font-semibold text-foreground tabular-nums">
                {formatInvestorMoney(trackingCombinedUsd, "USD")}
              </span>
              {lastDaily && Math.abs(lastDaily.cumulative - trackingCombinedUsd) > 0.5 && (
                <span className="ml-1 text-amber-600">(gráfico y North Star pueden diferir ±redondeo)</span>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {hasPeriod && (
          <Card className="glass-card border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Crecimiento por período (P1–P5)</CardTitle>
              <CardDescription className="text-[11px]">
                Barras = revenue del corte · línea = acumulado desde {INVESTOR_TRACKING_LABEL}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SafeChart>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={periodChart} margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 12, fontWeight: 600, fill: "hsl(var(--foreground))" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      yAxisId="period"
                      tickFormatter={formatUsdAxis}
                      tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                      width={48}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      yAxisId="cumPeriod"
                      orientation="right"
                      tickFormatter={formatUsdAxis}
                      tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                      width={52}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.[0]) return null;
                        const row = payload[0].payload as (typeof periodChart)[number];
                        return (
                          <div className="min-w-[220px] rounded-lg border border-white/10 bg-black/90 p-3 text-xs shadow-xl backdrop-blur-sm">
                            <p className="font-semibold mb-1">{row.periodLabel}</p>
                            <p>Período: {formatInvestorMoney(row.combinedUsd, "USD")}</p>
                            <p>Acumulado: {formatInvestorMoney(row.cumulativeCombinedUsd, "USD")}</p>
                            {row.pctChange !== null && (
                              <p className={cn("mt-1 font-medium", row.pctChange >= 0 ? "text-emerald-600" : "text-rose-600")}>
                                vs anterior: {row.pctChange > 0 ? "+" : ""}{row.pctChange}%
                              </p>
                            )}
                          </div>
                        );
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => (v === "combinedUsd" ? "Revenue período" : "Acumulado")} />
                    <Bar yAxisId="period" dataKey="combinedUsd" name="combinedUsd" maxBarSize={48} radius={[6, 6, 0, 0]}>
                      {periodBars.map((entry) => (
                        <Cell key={entry.id} fill={entry.isCurrent ? "hsl(38 92% 50%)" : "hsl(262 83% 58%)"} />
                      ))}
                    </Bar>
                    <Line
                      yAxisId="cumPeriod"
                      type="monotone"
                      dataKey="cumulativeCombinedUsd"
                      stroke="#f59e0b"
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: "#f59e0b", strokeWidth: 0 }}
                      name="cumulativeCombinedUsd"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </SafeChart>
              <div className="mt-3 grid grid-cols-5 gap-1">
                {reportingPeriodRevenue.map((p) => {
                  const st = getPeriodStatusFromRange(p.from, p.to, p.days);
                  return (
                    <div
                      key={p.period_id}
                      className={cn(
                        "rounded-md border px-1.5 py-1.5 text-center",
                        st.isCurrent ? "border-amber-500/40 bg-amber-500/10" : "border-border/40 bg-muted/20",
                      )}
                    >
                      <p className="text-[10px] font-bold">P{p.period_id}</p>
                      <p className="text-[9px] text-muted-foreground leading-tight mt-0.5">
                        {formatPeriodRange(p.from, p.to)}
                      </p>
                      <p className="text-[10px] font-semibold tabular-nums mt-1">
                        {formatInvestorMoney(p.revenue_combined_usd, "USD")}
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {hasWeekly && (
          <Card className="glass-card border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <LineChartIcon className="h-4 w-4 text-blue-500" />
                Tendencia semanal
              </CardTitle>
              <CardDescription className="text-[11px]">Últimas {weeklyChart.length} semanas · total USD equiv.</CardDescription>
            </CardHeader>
            <CardContent>
              <SafeChart>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={weeklyChart} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tickFormatter={formatUsdAxis}
                      tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                      width={48}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.[0]) return null;
                        const row = payload[0].payload as (typeof weeklyChart)[number];
                        return (
                          <div className="rounded-lg border border-white/10 bg-black/90 p-3 text-xs shadow-xl backdrop-blur-sm">
                            <p className="font-semibold">{row.weekLabel}</p>
                            <p className="tabular-nums mt-1">{formatInvestorMoney(row.combinedUsd, "USD")} total</p>
                            <p className="text-muted-foreground tabular-nums">
                              {formatInvestorMoney(row.usd, "USD")} + {formatInvestorMoney(row.ars, "ARS")}
                            </p>
                          </div>
                        );
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="combinedUsd"
                      stroke="hsl(217 91% 55%)"
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: "hsl(217 91% 55%)", strokeWidth: 0 }}
                      activeDot={{ r: 6 }}
                    />
                    <Bar dataKey="combinedUsd" fill="hsl(217 91% 55% / 0.2)" radius={[4, 4, 0, 0]} maxBarSize={36} />
                  </ComposedChart>
                </ResponsiveContainer>
              </SafeChart>
            </CardContent>
          </Card>
        )}

        {hasChurn && (
          <Card className={cn("glass-card border-border/60 shadow-sm", hasWeekly ? "lg:col-span-2" : "")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <UserX className="h-4 w-4 text-rose-500" />
                Suscripciones: altas, bajas y churn
              </CardTitle>
              <CardDescription className="text-[11px]">
                Barras = primer cobro (verde) y dejaron de pagar (rojo) · línea = pagadores del período · churn %
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SafeChart>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={churnChart} margin={{ top: 8, right: 20, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fontWeight: 600 }} tickLine={false} axisLine={false} />
                    <YAxis
                      yAxisId="subs"
                      tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                      width={36}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <YAxis
                      yAxisId="churn"
                      orientation="right"
                      tickFormatter={formatPctAxis}
                      tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                      width={40}
                      tickLine={false}
                      axisLine={false}
                      domain={[0, "auto"]}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.[0]) return null;
                        const row = payload[0].payload as (typeof churnChart)[number];
                        return (
                          <div className="min-w-[200px] rounded-lg border border-white/10 bg-black/90 p-3 text-xs shadow-xl backdrop-blur-sm">
                            <p className="font-semibold">{row.periodLabel}</p>
                            <p className="text-emerald-600">+{row.newSubs} altas</p>
                            <p className="text-rose-600">−{row.cancelled} bajas</p>
                            <p>Activas cierre: {row.activeAtEnd}</p>
                            <p>Churn: {row.churnRate.toFixed(2)}%</p>
                          </div>
                        );
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar yAxisId="subs" dataKey="newSubs" fill="#10b981" name="Altas" maxBarSize={32} radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="subs" dataKey="cancelled" fill="#f43f5e" name="Bajas" maxBarSize={32} radius={[4, 4, 0, 0]} />
                    <Line
                      yAxisId="subs"
                      type="monotone"
                      dataKey="activeAtEnd"
                      stroke="#3b82f6"
                      strokeWidth={2.5}
                      dot={{ r: 4 }}
                      name="Activas"
                    />
                    <Line
                      yAxisId="churn"
                      type="monotone"
                      dataKey="churnRate"
                      stroke="#a855f7"
                      strokeWidth={2}
                      strokeDasharray="4 3"
                      dot={{ r: 3, fill: "#a855f7" }}
                      name="Churn %"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </SafeChart>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}